import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Sign in with email and password
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { user: userCredential.user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Sign out
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create a new user (admin only function)
export const createUser = async (name, email, password, isAdmin = false, currentUser) => {
  try {
    // Check if current user is admin
    const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
    const currentUserData = currentUserDoc.data();
    
    if (!currentUserData.isAdmin) {
      throw new Error('Only admins can create new users');
    }
    
    // Create the user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update the user profile with the name
    await updateProfile(user, { displayName: name });
    
    // Create a user document in Firestore
    await setDoc(doc(db, 'users', user.uid), {
      name,
      email,
      isAdmin,
      createdAt: new Date(),
      createdBy: currentUser.uid,
      weddings: [] // Initialize empty weddings array
    });
    
    return { user, error: null };
  } catch (error) {
    return { user: null, error: error.message };
  }
};

// Reset password
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true, error: null };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

// Create a user document if it doesn't exist
export const createUserDocument = async (user) => {
  if (!user) return null;
  
  try {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log('Creating new user document for:', user.uid);
      const userData = {
        name: user.displayName || '',
        email: user.email || '',
        isAdmin: false,
        createdAt: new Date(),
        weddings: []
      };
      
      await setDoc(userRef, userData);
      return { ...userData, uid: user.uid };
    }
    
    return { ...userDoc.data(), uid: user.uid };
  } catch (error) {
    console.error('Error creating user document:', error);
    return null;
  }
};

// Get current user data including Firestore data
export const getCurrentUserData = async (user) => {
  if (!user) return null;
  
  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      return { ...userDoc.data(), uid: user.uid };
    }
    
    // If user document doesn't exist, create it
    return await createUserDocument(user);
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};
